import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import ContactForm from '../components/ContactForm';
import '../styles/pages/company.css';

const BOOK_URL = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

const REASONS = [
  { icon: 'fa-clock', title: 'Quick Response', text: 'Typically respond within 2 hours' },
  { icon: 'fa-shield-halved', title: 'Privacy First', text: 'Canadian data residency & secure consultations' },
  { icon: 'fa-lightbulb', title: 'No Sales Pressure', text: 'Just practical insights & custom roadmap' },
];

const FAQS = [
  {
    q: 'What happens during the free consultation?',
    a: "We'll discuss your current processes, identify automation opportunities, and provide a custom roadmap for implementation. No sales pressure, just practical insights tailored to your business needs.",
  },
  {
    q: 'How quickly can you implement a solution?',
    a: 'Timeline varies by complexity, but most solutions are deployed within 2-4 months. Simple automation like Digital Front Desk can be implemented in just 2-4 weeks.',
  },
  {
    q: 'Is my data secure with your solutions?',
    a: 'Absolutely. We follow privacy-by-design principles, maintain Canadian data residency, and ensure enterprise-grade security for all solutions. Your data never leaves Canada.',
  },
  {
    q: 'Do you provide ongoing support?',
    a: 'Yes, we offer comprehensive support packages including monitoring, updates, training, and optimization to ensure your automation continues to deliver maximum value.',
  },
  {
    q: 'What industries do you serve?',
    a: 'We specialize in healthcare, professional services, and technology companies. Our solutions are particularly effective for practices dealing with high call volumes, client intake, and document processing.',
  },
];

function Contact() {
  return (
    <>
      <PageHero
        id="contact-hero"
        className="co-hero co-hero--contact"
        eyebrow="Let's connect"
        title={<>Ready to <em>streamline</em> operations?</>}
        lead="Book a free 30-minute consultation and discover exactly how AI automation can save you 20+ hours weekly while improving efficiency across your entire organization."
        crumbs={[{ to: '/', label: 'Home' }, { label: 'Contact' }]}
        actions={<>
          <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
            <i className="fa-solid fa-calendar-check" aria-hidden="true" />
            Book a Demo
          </a>
          <a href="tel:+15484907101" className="btn btn-ghost btn-lg">
            <i className="fa-solid fa-phone" aria-hidden="true" />
            Call Directly
          </a>
        </>}
      >
        <aside className="co-consult" aria-labelledby="co-consult-title">
          <span className="co-consult__badge" aria-hidden="true"><i className="fa-solid fa-calendar-check" /></span>
          <dl className="co-consult__stats">
            <div><dt>Response Time</dt><dd>2h</dd></div>
            <div><dt>Free Consultation</dt><dd>30min</dd></div>
            <div><dt>Privacy First</dt><dd>100%</dd></div>
          </dl>
          <h2 className="co-consult__title" id="co-consult-title">Why Choose Our Consultation?</h2>
          <ul className="co-consult__list">
            {REASONS.map((r) => (
              <li key={r.title}>
                <span className="icon-tile icon-tile--sm"><i className={`fa-solid ${r.icon}`} aria-hidden="true" /></span>
                <div>
                  <h3>{r.title}</h3>
                  <p>{r.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </aside>
      </PageHero>

      {/* MESSAGE FORM */}
      <section id="message" className="section section--light co-message" aria-labelledby="contact-message-title">
        <div className="container">
          <div className="co-message__grid">
            <div className="co-message__intro">
              <SectionHeader
                align="stack"
                eyebrow="Send us a message"
                title={<>Tell us what to <em>automate</em></>}
                lead="Describe the task that eats your team's time. We'll reply with practical next steps, usually within 2 hours on business days."
                id="contact-message-title"
              />
              <ul className="ticks" data-reveal-stagger>
                <li>Free 30-minute consultation</li>
                <li>No commitment required</li>
                <li>Canadian data residency</li>
              </ul>
            </div>
            <div className="card card--flat co-message__card" data-reveal="right">
              <ContactForm idPrefix="cp" />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT METHODS */}
      <section className="section section--dark grain" aria-labelledby="contact-methods-title">
        <div className="container">
          <SectionHeader
            index="01"
            eyebrow="Choose your preferred method"
            title={<>Get in <em>touch</em></>}
            lead="Multiple ways to connect with our automation experts."
            id="contact-methods-title"
          />
          <div className="grid-3 co-methods" data-reveal-stagger>
            <article className="card card--feature card--dark co-method">
              <span className="icon-tile icon-tile--note"><i className="fa-solid fa-calendar" aria-hidden="true" /></span>
              <h3 className="card__title">Book a Demo</h3>
              <p className="card__text">Schedule a free 30-minute consultation to discuss your automation needs</p>
              <div className="card__foot">
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-magnetic>
                  Book a Demo <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </a>
              </div>
            </article>
            <article className="card card--feature card--dark co-method">
              <span className="icon-tile"><i className="fa-solid fa-phone" aria-hidden="true" /></span>
              <h3 className="card__title">Call Directly</h3>
              <p className="card__text">Speak directly with our automation experts for immediate assistance</p>
              <div className="card__foot">
                <a href="tel:+15484907101" className="co-method__link">+1 (548) 490&#8209;7101</a>
              </div>
            </article>
            <article className="card card--feature card--dark co-method">
              <span className="icon-tile"><i className="fa-solid fa-envelope" aria-hidden="true" /></span>
              <h3 className="card__title">Email Us</h3>
              <p className="card__text">Send us a detailed inquiry and we'll respond within 2 hours</p>
              <div className="card__foot">
                <a href="mailto:info@moneliautomation.com" className="co-method__link">
                  <span className="visually-hidden">Send Email: </span>info@moneliautomation.com
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section--light" aria-labelledby="contact-faq-title">
        <div className="container">
          <div className="co-faq">
            <div className="co-faq__head">
              <SectionHeader
                align="stack"
                index="02"
                eyebrow="Everything you need to know"
                title={<>Frequently asked <em>questions</em></>}
                lead="Common questions about our automation services."
                id="contact-faq-title"
              />
            </div>
            <div className="accordion co-accordion" id="faqAccordion" data-reveal-stagger>
              {FAQS.map((f, i) => {
                const id = `faq${i + 1}`;
                const open = i === 0;
                return (
                  <div className="accordion-item" key={id}>
                    <h3 className="accordion-header" id={`${id}-h`}>
                      <button
                        className={`accordion-button${open ? '' : ' collapsed'}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#${id}`}
                        aria-expanded={open ? 'true' : 'false'}
                        aria-controls={id}
                      >
                        <span className="co-accordion__num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                        {f.q}
                      </button>
                    </h3>
                    <div id={id} className={`accordion-collapse collapse${open ? ' show' : ''}`} aria-labelledby={`${id}-h`} data-bs-parent="#faqAccordion">
                      <div className="accordion-body">{f.a}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION & SOCIAL */}
      <section className="section section--sand" aria-labelledby="contact-location-title">
        <div className="container">
          <div className="co-location">
            <div>
              <p className="eyebrow" data-reveal><span className="eyebrow__num">03</span>Where we are</p>
              <h2 className="h2 co-location__title" id="contact-location-title" data-split>Located in Canada's <em>tech hub</em></h2>
              <ul className="co-location__list" data-reveal-stagger>
                <li>
                  <span className="icon-tile"><i className="fa-solid fa-location-dot" aria-hidden="true" /></span>
                  <div>
                    <h3>Waterloo, Ontario</h3>
                    <p>Heart of Canada's technology corridor, serving clients across North America</p>
                  </div>
                </li>
                <li>
                  <span className="icon-tile"><i className="fa-solid fa-flag" aria-hidden="true" /></span>
                  <div>
                    <h3>Canadian Data Residency</h3>
                    <p>All data processing and storage complies with Canadian privacy regulations</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="card card--feature co-social" data-reveal="right">
              <span className="co-social__mark" aria-hidden="true"><i className="fa-brands fa-linkedin-in" /></span>
              <h3 className="card__title">Connect With Us</h3>
              <p className="card__text">Follow us on LinkedIn for automation insights, case studies, and industry updates</p>
              <div className="card__foot">
                <a
                  href="https://www.linkedin.com/company/moneliautomation/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ink btn-lg"
                  aria-label="LinkedIn"
                >
                  <i className="fa-brands fa-linkedin" aria-hidden="true" />
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING (light, the footer carries the dark CTA band) */}
      <section className="section section--light co-closing" aria-labelledby="contact-closing-title">
        <div className="container">
          <div className="co-closing__inner">
            <div>
              <h2 className="h2 co-closing__title" id="contact-closing-title" data-split>Ready to get <em>started?</em></h2>
              <p className="lead" data-reveal data-delay="0.15">Join the growing number of professionals who've automated their operations and saved 20+ hours weekly.</p>
            </div>
            <div className="co-closing__actions" data-reveal data-delay="0.25">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
                Book a Demo
              </a>
              <Link to="/case-studies" className="btn btn-ghost btn-lg">
                View Success Stories <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;
